import { useState, useRef, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { AgentAnalytics } from "./AgentAnalytics";
import { TicketAnalytics } from "./TicketAnalytics";
import { SIZE } from "@metacrm/metacrm-material-ui/dist/InputField";
import DatePicker from "@metacrm/metacrm-material-ui/dist/DatePicker";
import { Menu } from "@metacrm/metacrm-material-ui/dist/Menu";
import { StyledCard } from "./ServiceReport.styles";
import LoadingFilter from "components/LoadingComponent/LoadingFilter";
import { useFetchWidgetConfig } from "../redux/hooks";
import { useOutletContext } from "react-router";
import { format } from "url";
import { useIntl } from "react-intl";

const defaultRange = "all";
const ServiceReportPage = () => {
  const datePickerRef = useRef(null);
  const [isTicketAnalyticsLoading, setIsTicketAnalyticsLoading] =
    useState(false);
  const [isAgentAnalyticsLoading, setIsAgentAnalyticsLoading] = useState(false);
  const isTicketOrAgentAnalyticsLoading =
    isTicketAnalyticsLoading || isAgentAnalyticsLoading;
  const [entityName] = useOutletContext();
  const [dateRange, setDateRange] = useState([null, null]);
  const [platform, setPlatform] = useState("all");
  const { formatMessage } = useIntl();

  const { widgetConfig, fetchWidgetConfig } = useFetchWidgetConfig();
  useEffect(() => {
    fetchWidgetConfig({ entityName });
  }, []);

  const filterList = useMemo(() => {
    const mutipleWidget =
      widgetConfig?.length > 1
        ? {
            label: "Widget",
            value: "widget",
            subMenu: [
              {
                children: "All",
                value: "widget",
              },
              ...widgetConfig?.map((item) => ({
                children: item.name,
                value: item._id,
              })),
            ],
          }
        : { label: "Widget", value: "widget" };

    const ticketSourceList = [
      { label: "All", value: "all" },
      { label: "Discord", value: "discord" },
      { label: "Support Center", value: "supportCenter" },
      { label: "Telegram", value: "telegram" },
      mutipleWidget,
    ];
    const isWidgetCheck = widgetConfig?.some((item) => item._id === platform);
    return ticketSourceList?.map((ticketSource) => {
      if (ticketSource?.subMenu) {
        return {
          children: ticketSource.label,
          value: ticketSource.value,
          type: "radioGroup",
          needIcon: true,
          checked: isWidgetCheck,
          subMenu: ticketSource.subMenu?.map((widget) => ({
            children: widget.children,
            value: widget.value,
            type: "radio",
            checked: platform === widget.value,
            func: (e) => setPlatform(e.value),
          })),
        };
      }
      return {
        children: ticketSource.label,
        value: ticketSource.value,
        type: "radio",
        checked: platform === ticketSource.value,
        func: (e) => setPlatform(e.value),
      };
    });
  }, [widgetConfig, platform]);

  return (
    <Box>
      <div className="title fade-up">
        {formatMessage({ id: "global.function.serviceReport" })}
      </div>

      <Box sx={{ position: "relative" }} className="fade-up-more">
        {isTicketOrAgentAnalyticsLoading && <LoadingFilter />}
        <StyledCard
          style={{ padding: "8px", margin: "12px 0", marginTop: "20px" }}
        >
          <Box className="endRow" gap="8px">
            <Menu
              overflow="auto"
              position="initial"
              itemList={filterList}
              isFilter
              filterText={formatMessage({ id: "global.filter.filter" })}
            />
            <DatePicker
              ref={datePickerRef}
              setDateRange={setDateRange}
              dateRange={dateRange}
              inputContainerStyle={{ height: "32px", minWidth: "180px" }}
              allText={formatMessage({ id: "global.filter.all" })}
              quickSelectOptions={[
                {
                  days: 1,
                  label: formatMessage({ id: "datePicker.range.day" }),
                },
                {
                  days: 7,
                  label: formatMessage({ id: "datePicker.range.week" }),
                },
                {
                  days: 30,
                  label: formatMessage({ id: "datePicker.range.month" }),
                },
                {
                  days: "all",
                  label: formatMessage({ id: "datePicker.range.all" }),
                },
                {
                  days: "custom",
                  label: formatMessage({ id: "datePicker.range.custom" }),
                },
              ]}
            />
          </Box>
        </StyledCard>
        <TicketAnalytics
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          platform={platform}
          isTicketAnalyticsLoading={isTicketAnalyticsLoading}
          setIsTicketAnalyticsLoading={setIsTicketAnalyticsLoading}
        />

        <AgentAnalytics
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          platform={platform}
          isAgentAnalyticsLoading={isAgentAnalyticsLoading}
          setIsAgentAnalyticsLoading={setIsAgentAnalyticsLoading}
        />
      </Box>
    </Box>
  );
};

export default ServiceReportPage;
